import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Address } from 'viem';

import { useClaimUnstakeMutation } from 'modules/liquidStaking/actions/claimUnstake';
import { useDelegateClaimMutation } from 'modules/pool/actions/delegateClaim';
import { RootState } from 'modules/store';

import { selectClaimLoadingState, setClaimLoading } from '../claimSlice';

interface IUseClaimResult {
  isLoading: boolean;
  isSuccess: boolean;
  claim: () => Promise<boolean>;
  resetClaim: () => void;
}

export function useClaim(
  poolAddress: Address,
  isLiquidStaking?: boolean,
): IUseClaimResult {
  const isMountedRef = useRef(true);

  const [
    delegateClaim,
    {
      isLoading: isDelegateClaimLoading,
      data: delegateData,
      reset: delegateReset,
    },
  ] = useDelegateClaimMutation();
  const [
    lsClaim,
    { isLoading: isLSClaimLoading, data: lsData, reset: lsReset },
  ] = useClaimUnstakeMutation();

  const { claim, isClaimLoading, data, reset } = useMemo(() => {
    if (isLiquidStaking) {
      return {
        claim: () => lsClaim({ poolAddress }),
        isClaimLoading: isLSClaimLoading,
        data: lsData,
        reset: lsReset,
      };
    }

    return {
      claim: delegateClaim,
      isClaimLoading: isDelegateClaimLoading,
      data: delegateData,
      reset: delegateReset,
    };
  }, [
    delegateClaim,
    delegateData,
    delegateReset,
    isDelegateClaimLoading,
    isLSClaimLoading,
    isLiquidStaking,
    lsClaim,
    lsData,
    lsReset,
    poolAddress,
  ]);

  const dispatch = useDispatch();

  const isClaimLoadingState = useSelector(state =>
    selectClaimLoadingState(state as RootState),
  );

  const isLoading = isClaimLoading || !!isClaimLoadingState?.[poolAddress];

  const handleClaim = useCallback(async () => {
    if (isLoading || !isMountedRef.current) {
      return false;
    }

    dispatch(setClaimLoading({ poolAddress, loading: true }));

    try {
      const { data } = await claim({ poolAddress });

      return !!data;
    } catch {
      return Promise.resolve(false);
    } finally {
      if (isMountedRef.current) {
        dispatch(setClaimLoading({ poolAddress, loading: false }));
      }
    }
  }, [claim, dispatch, isLoading, poolAddress]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    isLoading,
    isSuccess: !!data,
    claim: handleClaim,
    resetClaim: reset,
  };
}
